/**
 * @fileoverview First-touch and Last-touch Marketing Attribution Engine for PostBrix.
 * Captures and persists URL campaign parameters (UTMs, click IDs) safely in browser storage.
 */

export interface UtmParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  ref?: string;
  gclid?: string;
  fbclid?: string;
  landing_page?: string;
  first_touch_timestamp?: number;
  last_touch_timestamp?: number;
}

const FIRST_TOUCH_KEY = 'postbrix_first_touch_utm';
const LAST_TOUCH_KEY = 'postbrix_last_touch_utm';

/**
 * Capture UTM query parameters and referral identifiers (?ref=) from the current URL.
 * Persists first-touch attribution permanently in localStorage and updates last-touch in sessionStorage.
 */
export function captureAttributionFromUrl(): UtmParams | null {
  if (typeof window === 'undefined') return null;

  try {
    const url = new URL(window.location.href);
    const searchParams = url.searchParams;

    const rawRef =
      searchParams.get('ref') ??
      searchParams.get('ref_source') ??
      searchParams.get('ref_src') ??
      searchParams.get('referral') ??
      undefined;
    const ref = rawRef ? rawRef.trim() : undefined;

    const rawUtmSource = searchParams.get('utm_source') ?? undefined;
    // Fall back to ref parameter if utm_source is not explicitly set in the URL
    const utmSource = rawUtmSource || ref;

    const utmMedium = searchParams.get('utm_medium') ?? undefined;
    const utmCampaign = searchParams.get('utm_campaign') ?? undefined;
    const utmContent = searchParams.get('utm_content') ?? undefined;
    const utmTerm = searchParams.get('utm_term') ?? undefined;
    const gclid = searchParams.get('gclid') ?? undefined;
    const fbclid = searchParams.get('fbclid') ?? undefined;

    const hasCampaignParams = !!(
      utmSource ||
      utmMedium ||
      utmCampaign ||
      utmContent ||
      utmTerm ||
      ref ||
      gclid ||
      fbclid
    );

    const now = Date.now();

    if (hasCampaignParams) {
      const currentTouch: UtmParams = {
        ...(utmSource && { utm_source: utmSource }),
        ...(utmMedium && { utm_medium: utmMedium }),
        ...(utmCampaign && { utm_campaign: utmCampaign }),
        ...(utmContent && { utm_content: utmContent }),
        ...(utmTerm && { utm_term: utmTerm }),
        ...(ref && { ref }),
        ...(gclid && { gclid }),
        ...(fbclid && { fbclid }),
        landing_page: window.location.pathname,
        last_touch_timestamp: now,
      };

      // 1. First-touch attribution (preserve original entry source)
      const existingFirst = localStorage.getItem(FIRST_TOUCH_KEY);
      if (!existingFirst) {
        const firstTouch = {
          ...currentTouch,
          first_touch_timestamp: now,
        };
        localStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify(firstTouch));
      }

      // 2. Last-touch attribution (update on new campaign touch)
      sessionStorage.setItem(LAST_TOUCH_KEY, JSON.stringify(currentTouch));
      return currentTouch;
    }
  } catch (err) {
    console.error('[Analytics] Failed to capture UTM attribution:', err);
  }

  return getAttributionParams();
}

/**
 * Get current stored attribution parameters (preferring last-touch, falling back to first-touch).
 */
export function getAttributionParams(): UtmParams | null {
  if (typeof window === 'undefined') return null;

  try {
    const lastTouchStr = sessionStorage.getItem(LAST_TOUCH_KEY);
    if (lastTouchStr) {
      return JSON.parse(lastTouchStr) as UtmParams;
    }

    const firstTouchStr = localStorage.getItem(FIRST_TOUCH_KEY);
    if (firstTouchStr) {
      return JSON.parse(firstTouchStr) as UtmParams;
    }
  } catch (err) {
    console.error('[Analytics] Failed to read stored attribution:', err);
  }

  return null;
}
